import React, { useMemo } from 'react';
import { CalendarDays, ClipboardList, FileSpreadsheet, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const formatDate = (value) => {
  if (!value) return '—';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  return year && month && day ? `${day}/${month}/${year}` : value;
};

const statusLabel = (value) => ({ pending: 'Pendente', approved: 'Aprovado', rejected: 'Rejeitado' }[value] || value || '—');
const statusClassName = (value) => ({ pending: 'bg-amber-50 text-amber-700', approved: 'bg-emerald-50 text-emerald-700', rejected: 'bg-rose-50 text-rose-700' }[value] || 'bg-slate-100 text-slate-700');

export const PayrollVacationsTabContent = ({
  canManagePayroll,
  vacationYear,
  setVacationYear,
  employees,
  vacationsLoading,
  vacationsDashboard,
  vacationRequestForm,
  setVacationRequestForm,
  onCreateVacationRequest,
  vacationPolicyForm,
  setVacationPolicyForm,
  onSaveVacationPolicy,
  selectedVacationEmployeeNumber,
  setSelectedVacationEmployeeNumber,
  onUpdateVacationRequestStatus,
  onDownloadVacationReport,
}) => {
  const plans = vacationsDashboard?.individual_plans || [];
  const filteredPlans = useMemo(() => {
    if (selectedVacationEmployeeNumber === 'all') return plans;
    return plans.filter((plan) => plan.employee_number === selectedVacationEmployeeNumber);
  }, [plans, selectedVacationEmployeeNumber]);
  const activePlan = filteredPlans[0] || null;
  const requests = vacationsDashboard?.requests || [];
  const policy = vacationsDashboard?.policy || {};
  const activeRegime = policy.active_regime || policy.regimes?.[policy.active_regime_key] || {};
  const summary = vacationsDashboard?.summary || {};

  return (
    <div className="space-y-6" data-testid="payroll-vacations-tab">
      <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
        <Card className="rounded-2xl border-slate-200 shadow-sm" data-testid="vacations-policy-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><ShieldCheck className="h-4 w-4" /> Regra legal activa e parametrização</CardTitle>
            <CardDescription>
              Regime activo documentado e configurável. Saldo = direito anual menos dias aprovados.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              <div className="space-y-1">
                <Label className="text-xs">Ano de trabalho</Label>
                <Input type="number" value={vacationYear} onChange={(event) => setVacationYear(Number(event.target.value) || new Date().getFullYear())} data-testid="vacations-year-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Regime activo</Label>
                <Select value={vacationPolicyForm.active_regime_key || policy.active_regime_key || 'mozambique_law_13_2023_current'} onValueChange={(value) => setVacationPolicyForm((current) => ({ ...current, active_regime_key: value }))}>
                  <SelectTrigger data-testid="vacations-policy-regime-select"><SelectValue placeholder="Seleccionar regime" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mozambique_law_13_2023_current">Lei do Trabalho 13/2023 (actual)</SelectItem>
                    <SelectItem value="mozambique_law_23_2007_legacy">Lei 23/2007 (legado)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">1º ano</Label>
                <Input type="number" step="0.5" value={vacationPolicyForm.first_year_days} onChange={(event) => setVacationPolicyForm((current) => ({ ...current, first_year_days: event.target.value }))} data-testid="vacations-policy-first-year-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Anos subsequentes</Label>
                <Input type="number" step="0.5" value={vacationPolicyForm.subsequent_year_days} onChange={(event) => setVacationPolicyForm((current) => ({ ...current, subsequent_year_days: event.target.value }))} data-testid="vacations-policy-subsequent-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Prazo (dias por mês)</Label>
                <Input type="number" step="0.5" value={vacationPolicyForm.fixed_term_days_per_month} onChange={(event) => setVacationPolicyForm((current) => ({ ...current, fixed_term_days_per_month: event.target.value }))} data-testid="vacations-policy-fixed-term-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Faixa contrato a prazo</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" value={vacationPolicyForm.fixed_term_min_months} onChange={(event) => setVacationPolicyForm((current) => ({ ...current, fixed_term_min_months: event.target.value }))} data-testid="vacations-policy-fixed-term-min-input" />
                  <Input type="number" value={vacationPolicyForm.fixed_term_max_months} onChange={(event) => setVacationPolicyForm((current) => ({ ...current, fixed_term_max_months: event.target.value }))} data-testid="vacations-policy-fixed-term-max-input" />
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700" data-testid="vacations-policy-summary-card">
              <p><span className="font-semibold">Regra activa:</span> {activeRegime.label || '—'}</p>
              <p className="mt-1"><span className="font-semibold">Fonte:</span> {activeRegime.source || '—'}</p>
              <p className="mt-1"><span className="font-semibold">Saldo:</span> direito anual ({policy.balance_formula || 'entitlement_days - approved_days'})</p>
              <p className="mt-1"><span className="font-semibold">Notas:</span> {activeRegime.notes || '—'}</p>
            </div>
            {canManagePayroll && (
              <Button className="rounded-xl" onClick={onSaveVacationPolicy} data-testid="vacations-save-policy-button">Guardar parametrização</Button>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-slate-200 shadow-sm" data-testid="vacations-summary-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><FileSpreadsheet className="h-4 w-4" /> Resumo anual de férias</CardTitle>
            <CardDescription>Estado consolidado do ano seleccionado.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            {[
              ['Colaboradores', summary.employees_in_scope || 0],
              ['Pedidos', summary.requests_total || 0],
              ['Pendentes', summary.requests_pending || 0],
              ['Aprovados', summary.requests_approved || 0],
              ['Dias aprovados', summary.approved_days || 0],
              ['Dias gozados', summary.taken_days || 0],
              ['Dias pendentes', summary.pending_days || 0],
              ['Saldo agregado', summary.balance_days || 0],
            ].map(([label, value]) => (
              <div key={label} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-[0.16em] text-slate-500">{label}</p>
                <p className="mt-1 text-lg font-semibold text-slate-900">{value}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-[0.95fr_1.05fr]">
        <Card className="rounded-2xl border-slate-200 shadow-sm" data-testid="vacations-requests-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><ClipboardList className="h-4 w-4" /> Pedidos de Férias</CardTitle>
            <CardDescription>Fluxo mínimo: pendente → aprovado → rejeitado.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {canManagePayroll && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid="vacations-request-form">
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1 md:col-span-2">
                    <Label className="text-xs">Colaborador</Label>
                    <Select value={vacationRequestForm.employee_id} onValueChange={(value) => setVacationRequestForm((current) => ({ ...current, employee_id: value }))}>
                      <SelectTrigger data-testid="vacations-request-employee-select"><SelectValue placeholder="Seleccionar colaborador" /></SelectTrigger>
                      <SelectContent>
                        {employees.map((employee) => (
                          <SelectItem key={employee.id} value={employee.id}>{employee.employee_number} · {employee.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Início</Label>
                    <Input type="date" value={vacationRequestForm.start_date} onChange={(event) => setVacationRequestForm((current) => ({ ...current, start_date: event.target.value }))} data-testid="vacations-request-start-date" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Fim</Label>
                    <Input type="date" value={vacationRequestForm.end_date} onChange={(event) => setVacationRequestForm((current) => ({ ...current, end_date: event.target.value }))} data-testid="vacations-request-end-date" />
                  </div>
                  <div className="space-y-1 md:col-span-2">
                    <Label className="text-xs">Observações</Label>
                    <Textarea rows={3} value={vacationRequestForm.notes} onChange={(event) => setVacationRequestForm((current) => ({ ...current, notes: event.target.value }))} data-testid="vacations-request-notes" />
                  </div>
                </div>
                <Button className="mt-3 rounded-xl" onClick={onCreateVacationRequest} data-testid="vacations-create-request-button">Criar pedido</Button>
              </div>
            )}

            <div className="space-y-3" data-testid="vacations-requests-list">
              {requests.length ? requests.map((request) => (
                <div key={request.id} className="rounded-2xl border border-slate-200 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{request.employee_name}</p>
                      <p className="text-xs text-slate-500">{request.employee_number} · {request.store_location || 'Empresa geral'}</p>
                    </div>
                    <Badge className={statusClassName(request.status)}>{statusLabel(request.status)}</Badge>
                  </div>
                  <div className="mt-2 grid gap-2 text-xs text-slate-600 sm:grid-cols-3">
                    <p><span className="font-semibold">Início:</span> {formatDate(request.start_date)}</p>
                    <p><span className="font-semibold">Fim:</span> {formatDate(request.end_date)}</p>
                    <p><span className="font-semibold">Dias:</span> {request.requested_days}</p>
                  </div>
                  {(request.notes || request.reviewer_notes) && <p className="mt-2 text-xs text-slate-500">{request.reviewer_notes || request.notes}</p>}
                  {canManagePayroll && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" className="rounded-lg" onClick={() => onUpdateVacationRequestStatus(request.id, 'pending')} data-testid={`vacations-status-pending-${request.id}`}>Pendente</Button>
                      <Button variant="outline" size="sm" className="rounded-lg" onClick={() => onUpdateVacationRequestStatus(request.id, 'approved')} data-testid={`vacations-status-approved-${request.id}`}>Aprovar</Button>
                      <Button variant="outline" size="sm" className="rounded-lg" onClick={() => onUpdateVacationRequestStatus(request.id, 'rejected')} data-testid={`vacations-status-rejected-${request.id}`}>Rejeitar</Button>
                    </div>
                  )}
                </div>
              )) : (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">Sem pedidos de férias registados para {vacationYear}.</div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-slate-200 shadow-sm" data-testid="vacations-plans-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><CalendarDays className="h-4 w-4" /> Plano de Férias por Colaborador</CardTitle>
            <CardDescription>Saldo, dias gozados, aprovados e pendentes com base no regime activo.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <Label className="text-xs">Filtro do plano individual</Label>
              <Select value={selectedVacationEmployeeNumber} onValueChange={setSelectedVacationEmployeeNumber}>
                <SelectTrigger data-testid="vacations-plan-employee-select"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os colaboradores</SelectItem>
                  {plans.map((plan) => (
                    <SelectItem key={plan.employee_number} value={plan.employee_number}>{plan.employee_number} · {plan.employee_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {activePlan && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="vacations-individual-plan-card">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h4 className="text-base font-semibold text-slate-900">{activePlan.employee_name}</h4>
                    <p className="text-xs text-slate-500">{activePlan.employee_number} · {activePlan.role || '—'} · {activePlan.store_location || 'Empresa geral'}</p>
                  </div>
                  <Badge variant="outline">{activePlan.applicable_regime_label || 'Regime activo'}</Badge>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {[
                    ['Direito anual', activePlan.entitlement_days],
                    ['Aprovados', activePlan.approved_days],
                    ['Gozados', activePlan.taken_days],
                    ['Pendentes', activePlan.pending_days],
                    ['Saldo', activePlan.balance_days],
                    ['Admissão', formatDate(activePlan.hire_date)],
                    ['Janela legal', `${formatDate(activePlan.service_window_start)} → ${formatDate(activePlan.service_window_end)}`],
                    ['Base cálculo', activePlan.calculation_basis || '—'],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-xl border border-white bg-white p-3">
                      <p className="text-xs uppercase tracking-[0.16em] text-slate-500">{label}</p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3" data-testid="vacations-general-plan-list">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Plano Geral de Férias</p>
                  <p className="text-xs text-slate-500">Pedidos organizados para fiscalização e planeamento interno.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="rounded-lg" onClick={() => onDownloadVacationReport('requests', 'pdf')} data-testid="vacations-report-requests-pdf-button">Pedidos PDF</Button>
                  <Button variant="outline" size="sm" className="rounded-lg" onClick={() => onDownloadVacationReport('plan-individual', 'pdf')} disabled={selectedVacationEmployeeNumber === 'all'} data-testid="vacations-report-individual-pdf-button">Plano individual PDF</Button>
                  <Button variant="outline" size="sm" className="rounded-lg" onClick={() => onDownloadVacationReport('plan-general', 'xlsx')} data-testid="vacations-report-general-xlsx-button">Plano geral XLSX</Button>
                  <Button variant="outline" size="sm" className="rounded-lg" onClick={() => onDownloadVacationReport('balance-summary', 'xlsx')} data-testid="vacations-report-balance-xlsx-button">Saldo XLSX</Button>
                </div>
              </div>
              {(vacationsDashboard?.general_plan || []).length ? vacationsDashboard.general_plan.map((item) => (
                <div key={item.id} className="grid gap-2 rounded-2xl border border-slate-200 p-3 text-sm sm:grid-cols-[1.15fr_0.9fr_0.65fr_0.5fr]">
                  <div>
                    <p className="font-semibold text-slate-900">{item.employee_name}</p>
                    <p className="text-xs text-slate-500">{item.employee_number} · {item.store_location || 'Empresa geral'}</p>
                  </div>
                  <div className="text-xs text-slate-600">
                    <p><span className="font-semibold">Início:</span> {formatDate(item.start_date)}</p>
                    <p><span className="font-semibold">Fim:</span> {formatDate(item.end_date)}</p>
                  </div>
                  <div className="text-xs text-slate-600">
                    <p><span className="font-semibold">Dias:</span> {item.requested_days}</p>
                  </div>
                  <div className="flex items-center sm:justify-end">
                    <Badge className={statusClassName(item.status)}>{statusLabel(item.status)}</Badge>
                  </div>
                </div>
              )) : (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">Sem plano geral registado para {vacationYear}.</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {vacationsLoading && <div className="text-sm text-slate-500">A actualizar férias e relatórios...</div>}
    </div>
  );
};
